import { ConversionStatus, Lfa_Conversion_Details } from '~/graphql/types';
import { reporter } from '~/hooks/useErrorReporter';
import {
  analyzeAppointmentDate,
  type AppointmentDateCheck,
} from '../dateUtils/index';

export type CardStatus =
  | 'prepareForAppointment'
  | 'waitingForFeedback'
  | 'expiring'
  | 'completed'
  | 'rejected';

type FrontendStatusArgs = {
  status: ConversionStatus;
  appointmentDate: Lfa_Conversion_Details['appointmentDate'];
  daysToAutoAccept: number;
  hasFinalFeedback: boolean;
  hasServiceFeedback: boolean;
};

const EXPIRING_THRESHOLD_DAYS = 3;

/**
 * Determines if conversion is in waiting for feedback state
 */
const isWaitingForFeedback = (
  dateCheck: AppointmentDateCheck,
  hasServiceFeedback: boolean,
  hasFinalFeedback: boolean,
): boolean => {
  // Past appointments are always waiting for feedback
  if (dateCheck.isInPast) {
    return true;
  }

  // Future appointments with service feedback but no final feedback
  return dateCheck.isInFuture && hasServiceFeedback && !hasFinalFeedback;
};

/**
 * Handles the complex logic for pending conversions
 */
const getPendingStatus = (
  dateCheck: AppointmentDateCheck,
  daysToAutoAccept: number,
  hasServiceFeedback: boolean,
  hasFinalFeedback: boolean,
): CardStatus => {
  // Check if expiring soon
  if (daysToAutoAccept <= EXPIRING_THRESHOLD_DAYS) {
    return 'expiring';
  }

  // Check if waiting for feedback
  if (isWaitingForFeedback(dateCheck, hasServiceFeedback, hasFinalFeedback)) {
    return 'waitingForFeedback';
  }

  // Default to preparing for appointment
  return 'prepareForAppointment';
};

/**
 * Converts backend ConversionStatus to frontend CardStatus
 *
 * @param status - Backend conversion status
 * @param appointmentDate - Appointment date object
 * @param daysToAutoAccept - Days remaining until auto-acceptance
 * @param hasFinalFeedback - Whether final feedback has been provided
 * @param hasServiceFeedback - Whether service feedback has been provided
 * @returns Frontend card status for UI display
 */
const getFrontendStatus = ({
  status,
  appointmentDate,
  daysToAutoAccept,
  hasFinalFeedback,
  hasServiceFeedback,
}: FrontendStatusArgs): CardStatus => {
  const dateCheck = analyzeAppointmentDate(appointmentDate);

  switch (status) {
    case ConversionStatus.Accepted:
      return 'completed';

    case ConversionStatus.RejectedServices:
    case ConversionStatus.RejectedAppointmentFailed:
    case ConversionStatus.Cancelled:
      return 'rejected';

    case ConversionStatus.Pending:
      return getPendingStatus(
        dateCheck,
        daysToAutoAccept,
        hasServiceFeedback,
        hasFinalFeedback,
      );

    default:
      reporter.captureException(
        new Error(`Unhandled ConversionStatus: ${status}`),
      );
      return 'prepareForAppointment';
  }
};

export default getFrontendStatus;
